// Minimal hash-based history sync — no router library. The SPA's "view"
// (top-level tab, optional selected record, optional admin subtab) is
// mirrored 1:1 into `location.hash` so the browser Back/Forward buttons
// walk through app views instead of leaving the site.
//
// Route map:
//   #/dashboard              #/companies            #/companies/<id>
//   #/people                 #/deals                 #/activities
//   #/leads                  #/leads/<id>             #/issues
//   #/issues/<id>            #/admin/<subtab>         #/settings
//   #/emails                 #/reports               #/sites
//   #/sites/<id>             #/owners                #/owners/<id>
//
// `owners` is Hotels' main-nav view of kind=owner companies (see Header's
// nav swap) — a thin wrapper around the same CompanyDetail/companyRollup
// view companies/<id> already used for an owner row; #/companies/<id> stays
// the canonical deep link (and the only nav path in every other vertical).
//
// Convention used everywhere this module is consumed: user-triggered
// navigation (tab click, opening a detail, back-to-list) calls `push`, one
// history entry per view change. Programmatic redirects (post-login landing,
// logout, bouncing off a stale/invalid id) call `replace` so Back can't get
// trapped in a loop. Create/edit modals are never represented here — they
// close on Escape/click-outside already, independent of history.

import { navigationGuards } from './navigationGuard';

export type TabId =
  | 'my-day'
  | 'happy-code'
  | 'happy-code-queue'
  | 'feedback'
  | 'dispatch'
  | 'dashboard'
  | 'companies'
  | 'people'
  | 'deals'
  | 'activities'
  | 'leads'
  | 'issues'
  | 'admin'
  | 'settings'
  | 'emails'
  | 'reports'
  | 'sites'
  | 'owners';

const VALID_TABS: readonly TabId[] = [
  'my-day',
  'happy-code',
  'happy-code-queue',
  'dispatch',
  'dashboard',
  'companies',
  'people',
  'deals',
  'activities',
  'leads',
  'issues',
  'admin',
  'settings',
  'emails',
  'reports',
  'sites',
  'owners'
];

type RouteTab =
  | { tab: 'feedback'; token: string }
  | { tab: 'companies'; id?: number }
  | { tab: 'deals'; id?: number }
  | { tab: 'people'; id?: number }
  | { tab: 'activities'; id?: number }
  | { tab: 'leads'; id?: number }
  | { tab: 'issues'; id?: number }
  | { tab: 'sites'; id?: number }
  | { tab: 'owners'; id?: number }
  | { tab: 'admin'; sub?: string }
  | { tab: Exclude<TabId, 'feedback' | 'companies' | 'deals' | 'people' | 'activities' | 'leads' | 'issues' | 'sites' | 'owners' | 'admin'> };

// `vertical` (`'all'` or a vertical id as a string) is the app-wide scope
// selection — carried as a `?vertical=` query param on every route, not just
// the dashboard's, so it's shareable/bookmarkable and survives reload on any
// page. It's the single source of truth for scope: session.selectedVertical
// (session.svelte.ts) is a read-only derived view over `route.current.vertical`.
export type Route = RouteTab & { vertical?: string; filters?: Record<string, string> };

function toHash(r: Route): string {
  let path: string;
  if (r.tab === 'feedback') return `#/feedback/${encodeURIComponent(r.token)}`;
  if (r.tab === 'companies') path = r.id ? `#/companies/${r.id}` : '#/companies';
  else if (r.tab === 'deals' || r.tab === 'people' || r.tab === 'activities') path = r.id ? `#/${r.tab}/${r.id}` : `#/${r.tab}`;
  else if (r.tab === 'leads') path = r.id ? `#/leads/${r.id}` : '#/leads';
  else if (r.tab === 'issues') path = r.id ? `#/issues/${r.id}` : '#/issues';
  else if (r.tab === 'sites') path = r.id ? `#/sites/${r.id}` : '#/sites';
  else if (r.tab === 'owners') path = r.id ? `#/owners/${r.id}` : '#/owners';
  else if (r.tab === 'admin') path = r.sub ? `#/admin/${r.sub}` : '#/admin';
  else path = `#/${r.tab}`;
  const params = new URLSearchParams(r.filters);
  if (r.vertical) params.set('vertical', r.vertical);
  return params.size ? `${path}?${params}` : path;
}

function parseHash(hash: string): Route {
  const raw = hash.replace(/^#\/?/, '');
  const [pathPart, queryPart] = raw.split('?');
  const parts = pathPart.split('/').filter(Boolean);
  const [tab, sub] = parts;
  if (tab === 'feedback') return { tab: 'feedback', token: sub || '' };
  const vertical = queryPart ? (new URLSearchParams(queryPart).get('vertical') ?? undefined) : undefined;
  const params = new URLSearchParams(queryPart);
  params.delete('vertical');
  const filters = ['issues', 'people', 'activities'].includes(tab) ? Object.fromEntries(params) : undefined;
  if (tab === 'companies' || tab === 'deals' || tab === 'people' || tab === 'activities' || tab === 'leads' || tab === 'issues' || tab === 'sites' || tab === 'owners') {
    const id = sub ? Number(sub) : undefined;
    return { tab, id: id !== undefined && Number.isSafeInteger(id) && id > 0 ? id : undefined, vertical, filters };
  }
  if (tab === 'admin') return { tab: 'admin', sub: sub || undefined, vertical };
  if ((VALID_TABS as string[]).includes(tab)) {
    return { tab: tab as Exclude<TabId, 'feedback' | 'companies' | 'leads' | 'issues' | 'sites' | 'owners' | 'admin'>, vertical };
  }
  return { tab: 'dashboard', vertical };
}

class RouteState {
  current = $state<Route>(parseHash(location.hash));

  // Plain tab switches (sidebar nav, search-result picks, "+New", detail
  // views, ...) construct a `Route` without a `vertical` — carry the
  // currently-selected scope forward by default so navigating between pages
  // doesn't silently reset it. Callers that are themselves changing the
  // scope (Header's switcher, dashboard scope chips) pass `vertical`
  // explicitly and that wins.
  private withCarriedVertical(r: Route): Route {
    const filters = ['issues', 'people', 'activities'].includes(r.tab) && r.tab === this.current.tab && r.filters === undefined ? this.current.filters : r.filters;
    return { ...r, filters, vertical: r.vertical ?? this.current.vertical };
  }

  // User-initiated navigation — one new history entry per view change.
  // Publish page signals ONLY in onAccepted, never before push: a same-page
  // create signal can otherwise reset the very draft the guard is protecting.
  // The callback runs once, after approved modal closure and before committing
  // the destination. It also runs for accepted same-hash actions (no new entry).
  push(r: Route, onAccepted?: () => void) {
    const merged = this.withCarriedVertical(r);
    const hash = toHash(merged);
    if (!onAccepted && hash === toHash(this.current) && location.hash === hash) return;
    navigationGuards.request(() => {
      onAccepted?.();
      this.current = merged;
      if (location.hash !== hash) history.pushState(null, '', hash);
    });
  }

  // Programmatic redirects (post-login landing, logout, invalid/stale
  // routes) — replaces the current entry so Back can't loop onto a dead view.
  replace(r: Route) {
    // Security redirects/logouts must invalidate any previously queued intent.
    navigationGuards.cancelPending();
    const merged = this.withCarriedVertical(r);
    this.current = merged;
    const hash = toHash(merged);
    if (location.hash !== hash) history.replaceState(null, '', hash);
  }
}

export const route = new RouteState();

// Browser Back/Forward (popstate) and any direct hash edits (hashchange) —
// re-parse from location.hash rather than trusting the event, since both
// fire for the same "the fragment changed under us" case.
function syncFromLocation() {
  const target = parseHash(location.hash);
  const targetHash = toHash(target);
  const currentHash = toHash(route.current);
  if (targetHash === currentHash) return;
  if (!navigationGuards.hasDirty() && !navigationGuards.isPending()) {
    navigationGuards.request(() => { route.current = target; });
    return;
  }
  // Traversal/hash edits have already moved the browser URL, but not our
  // rendered route. Restore it without emitting another history event. On
  // acceptance push the requested destination once. This deliberately rewrites
  // the traversed entry rather than history.go()-replaying a potentially unknown
  // pre-login history stack. Back+hashchange's second event is then a no-op.
  history.replaceState(history.state, '', currentHash);
  navigationGuards.request(() => {
    route.current = target;
    if (location.hash !== targetHash) history.pushState(null, '', targetHash);
  });
}
window.addEventListener('popstate', syncFromLocation);
window.addEventListener('hashchange', syncFromLocation);
window.addEventListener('beforeunload', (event) => {
  if (!navigationGuards.hasDirty()) return;
  event.preventDefault();
  event.returnValue = '';
});
